import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, theme, Alert, Drawer } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  HomeOutlined,
  CoffeeOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  PieChartOutlined,
  WalletOutlined,
  CarOutlined,
  FireOutlined,
  SettingOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import useConfiguracaoStore from '../store/configuracaoStore';
import ErrorBoundary from './ErrorBoundary';

const { Sider, Header, Content } = Layout;
const MODULO_POR_ROTA = {
  '/pousada': ['modulo_pousada'],
  '/restaurante': ['modulo_restaurante', 'modulo_pizzaria'],
  '/cozinha': ['modulo_restaurante', 'modulo_pizzaria'],
  '/delivery': ['modulo_delivery'],
  '/caixa': ['modulo_caixa'],
  '/financeiro': ['modulo_financeiro'],
  '/estoque': ['modulo_estoque'],
};

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [avisoConfig, setAvisoConfig] = useState(null);
  const [bannerFechado, setBannerFechado] = useState(false);
  const { configuracao, carregar: carregarConfig } = useConfiguracaoStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, logout } = useAuthStore();
  const { token } = theme.useToken();

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const atualizarLayout = () => {
      setIsMobile(media.matches);
      if (!media.matches) setMobileMenuOpen(false);
    };

    atualizarLayout();
    media.addEventListener('change', atualizarLayout);
    return () => media.removeEventListener('change', atualizarLayout);
  }, []);

  // Carrega configurações via store global (compartilhado com Dashboard, Relatorios, etc.)
  useEffect(() => {
    if (!usuario) return;
    carregarConfig().then(() => {
      const cfg = useConfiguracaoStore.getState().configuracao || {};
      // Aviso de config incompleta apenas para admin/gerente
      if (['admin', 'gerente'].includes(usuario?.cargo)) {
        const faltando = [];
        if (!cfg?.nome_estabelecimento) faltando.push('nome do estabelecimento');
        if (!cfg?.whatsapp_pedidos) faltando.push('WhatsApp para pedidos');
        if (!cfg?.chave_pix) faltando.push('chave PIX');
        if (faltando.length > 0) {
          setAvisoConfig(`Configure ${faltando.join(', ')} em Configurações para o cardápio online funcionar corretamente.`);
        }
      }
    });
  }, [usuario, carregarConfig]);

  useEffect(() => {
    if (!usuario) return undefined;
    const atualizarModulos = () => carregarConfig({ force: true });
    const intervalo = window.setInterval(atualizarModulos, 30000);
    window.addEventListener('focus', atualizarModulos);
    return () => {
      window.clearInterval(intervalo);
      window.removeEventListener('focus', atualizarModulos);
    };
  }, [usuario, carregarConfig]);

  // Módulo Pousada: visível apenas para negócios que têm hospedagem
  const algumModuloAtivo = (modulos = []) => (
    Boolean(configuracao) && modulos.some(nome => configuracao[nome] === true)
  );
  useEffect(() => {
    const modulos = MODULO_POR_ROTA[location.pathname];
    const rotaAtiva = !modulos || modulos.some(nome => configuracao?.[nome] !== false);
    if (configuracao && !rotaAtiva) {
      navigate('/');
    }
  }, [configuracao, location.pathname, navigate]);

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/pousada', icon: <HomeOutlined />, label: 'Pousada', modulos: ['modulo_pousada'] },
    { key: '/restaurante', icon: <CoffeeOutlined />, label: 'Restaurante', modulos: ['modulo_restaurante', 'modulo_pizzaria'] },
    { key: '/delivery', icon: <CarOutlined />, label: 'Delivery', modulos: ['modulo_delivery'] },
    { key: '/cozinha', icon: <FireOutlined />, label: 'Cozinha', modulos: ['modulo_restaurante', 'modulo_pizzaria'] },
    { key: '/caixa', icon: <WalletOutlined />, label: 'Caixa', modulos: ['modulo_caixa'] },
    { key: '/financeiro', icon: <BarChartOutlined />, label: 'Financeiro', modulos: ['modulo_financeiro'] },
    { key: '/estoque', icon: <ShoppingCartOutlined />, label: 'Estoque', modulos: ['modulo_estoque'] },
    { key: '/clientes', icon: <UserOutlined />, label: 'Clientes' },
    { key: '/relatorios', icon: <PieChartOutlined />, label: 'Relatórios' },
    { key: '/escala', icon: <CalendarOutlined />, label: 'Escala' },
    { key: '/usuarios', icon: <TeamOutlined />, label: 'Usuários' },
    { key: '/configuracoes', icon: <SettingOutlined />, label: 'Configurações' },
  ].filter(item => !item.modulos || algumModuloAtivo(item.modulos));

  const cargosMenu = {
    '/': ['admin', 'gerente'],
    '/pousada': ['admin', 'gerente', 'recepcionista'],
    '/restaurante': ['admin', 'gerente', 'garcom', 'recepcionista', 'caixa'],
    '/delivery': ['admin', 'gerente', 'caixa', 'entregador'],
    '/cozinha': ['admin', 'gerente', 'cozinheiro', 'caixa'],
    '/caixa': ['admin', 'gerente', 'caixa'],
    '/financeiro': ['admin', 'gerente'],
    '/estoque': ['admin', 'gerente'],
    '/clientes': ['admin', 'gerente', 'caixa'],
    '/relatorios': ['admin', 'gerente'],
    '/escala': ['admin', 'gerente'],
    '/usuarios': ['admin', 'gerente'],
    '/configuracoes': ['admin', 'gerente'],
  };
  const menuPermitido = menuItems.filter(item => cargosMenu[item.key]?.includes(usuario?.cargo));

  const userMenu = {
    items: [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Sair',
        danger: true,
        onClick: () => {
          logout();
          navigate('/login');
        },
      },
    ],
  };

  const menu = (
    <>
      <div style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: collapsed && !isMobile ? 14 : 20,
        fontWeight: 700,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        {collapsed && !isMobile ? 'UB' : 'Unibox'}
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        style={{ background: '#1a1a2e', borderRight: 0 }}
        items={menuPermitido}
        onClick={({ key }) => {
          navigate(key);
          setMobileMenuOpen(false);
        }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{ background: '#1a1a2e' }}
        >
          {menu}
        </Sider>
      )}

      <Drawer
        placement="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        width={280}
        styles={{
          body: { padding: 0, background: '#1a1a2e' },
          header: { display: 'none' },
          content: { background: '#1a1a2e' },
        }}
      >
        {menu}
      </Drawer>

      <Layout>
        <Header style={{
          padding: isMobile ? '0 12px' : '0 24px',
          background: token.colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => isMobile ? setMobileMenuOpen(true) : setCollapsed(!collapsed)}
            style={{ fontSize: 18, width: isMobile ? 44 : 64, height: 64 }}
          />

          <Dropdown menu={userMenu} placement="bottomRight">
            <div style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
              maxWidth: isMobile ? 'calc(100vw - 76px)' : 'none',
            }}>
              <Avatar style={{ background: '#0f3460' }} icon={<UserOutlined />} />
              <span>{usuario?.nome || 'Usuário'}</span>
            </div>
          </Dropdown>
        </Header>

        <Content style={{
          margin: isMobile ? 8 : 24,
          padding: isMobile ? 12 : 24,
          background: token.colorBgContainer,
          borderRadius: isMobile ? 0 : 8,
          minHeight: 280,
          overflowX: 'hidden',
        }}>
          {avisoConfig && !bannerFechado && (
            <Alert
              type="warning"
              showIcon
              closable
              onClose={() => setBannerFechado(true)}
              message="Configuração incompleta"
              description={
                <span>
                  {avisoConfig}{' '}
                  <button
                    type="button"
                    onClick={() => { navigate('/configuracoes'); setBannerFechado(true); }}
                    style={{ fontWeight: 600, border: 0, background: 'transparent', color: '#1677ff', padding: 0, cursor: 'pointer' }}
                  >
                    Ir para Configurações →
                  </button>
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
}
